import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AnalyticsApiService, { DailyMetrics } from '../../services/analyticsApi';
import { DateRangePicker } from './DateRangePicker';
import { DateRange } from 'react-day-picker';
import { parseLocalDate } from '../../utils/dateParser';

interface MessageVolumeChartProps {
  className?: string;
  days?: 7 | 14 | 30;
  showControls?: boolean;
}

export function MessageVolumeChart({ className, days: propDays, showControls = true }: MessageVolumeChartProps) {
  const [data, setData] = useState<DailyMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize date range to last 7 days (normalized to midnight)
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    
    const start = new Date();
    start.setDate(start.getDate() - (propDays || 7));
    start.setHours(0, 0, 0, 0);
    
    return { from: start, to: end };
  });

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Message Volume Chart - Fetching data for date range:', dateRange);
      if (dateRange?.from && dateRange?.to) {
        // Normalize dates to start/end of day
        const startOfDay = new Date(dateRange.from);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(dateRange.to);
        endOfDay.setHours(23, 59, 59, 999);
        
        console.log('Message Volume Chart - Normalized dates:', { start: startOfDay, end: endOfDay });
        
        const metrics = await AnalyticsApiService.getDailyMetrics(
          startOfDay.toISOString(),
          endOfDay.toISOString()
        );
        console.log('Message Volume Chart - Received metrics:', metrics.length);
        setData(metrics);
      } else {
        const metrics = await AnalyticsApiService.getDailyMetrics(propDays || 7);
        setData(metrics);
      }
    } catch (error) {
      console.error('Failed to fetch message volume data:', error);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('Message Volume Chart - dateRange changed:', dateRange);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, dateRange?.to]);

  // Auto-refresh every hour
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 3600000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, dateRange?.to]);

  const formatXAxis = (dateStr: string) => {
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const date = parseLocalDate(label);
      return (
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-amber-200 dark:border-amber-700 rounded-xl p-3 shadow-xl">
          <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xs mb-2">
            {date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
          <p className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-amber-600 dark:text-amber-400 text-lg">
            {payload[0].value.toLocaleString()} messages
          </p>
        </div>
      );
    }
    return null;
  };

  const total = data.reduce((sum, d) => sum + d.message_count, 0);
  const avg = data.length > 0 ? Math.round(total / data.length) : 0;
  const max = data.length > 0 ? Math.max(...data.map(d => d.message_count)) : 0;

  return (
    <Card className={`bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200/50 dark:border-gray-800/50 shadow-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
                  Message Volume
                </h3>
                <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                  Total messages sent per day
                </p>
              </div>
            </div>
            
            {/* Summary Stats */}
            <div className="flex items-center gap-6 mt-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-amber-600 dark:text-amber-400">
                  {total.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Daily Avg</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                  {avg.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Peak Day</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                  {max.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Date Range Picker */}
          {showControls && (
            <DateRangePicker 
              value={dateRange}
              onChange={setDateRange}
              showDateText={false}
              className="w-auto"
            />
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="p-6 pt-4">
        {isLoading ? (
          <Skeleton className="h-64 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
        ) : error ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-red-500 dark:text-red-400">{error}</p>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="messageGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.6}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                  stroke="#e5e7eb"
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                  stroke="#e5e7eb"
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="message_count" 
                  fill="url(#messageGradient)"
                  radius={[8, 8, 0, 0]}
                  maxBarSize={60}
                  animationDuration={1000}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}
